import { makeAutoObservable } from 'mobx';
import IGeoCoordinate from '../interfaces/IGeoCoordinate';
import ILoadingProps, { ILoadingPropsDispatcher } from '../interfaces/ILoadingProps';
import ISaleComp, { ISaleCompState } from '../interfaces/ISaleComp';
import SaleCompsApiService from '../services/saleCompsApiService';

export default class SaleCompStore {
    saleComps: ISaleComp[] = [];
    isSaleCompsLoading: ILoadingProps = {};
    editedSaleCompId: number | null = null;
    radiusSaleCompIds: number[] = [];

    constructor(
        private saleCompsApiService: SaleCompsApiService,
    ) {
        makeAutoObservable(this);
    }

    setSaleComps(value: ISaleComp[]) {
        this.saleComps = value;
    }

    setIsSaleCompsLoading(): ILoadingPropsDispatcher {
        return {
            fetching: (value: boolean) => this.isSaleCompsLoading.fetching = value,
            creating: (value: boolean) => this.isSaleCompsLoading.creating = value,
            updating: (value: boolean) => this.isSaleCompsLoading.updating = value,
            deleting: (value: boolean) => this.isSaleCompsLoading.deleting = value,
        };
    }

    setEditedSaleCompId(value: number | null) {
        this.editedSaleCompId = value;
    }

    setRadiusSaleCompIds(value: number[]) {
        this.radiusSaleCompIds = value;
    }

    initialize() {
        this.setIsSaleCompsLoading().fetching(true);
        this.saleCompsApiService.getSaleComps().then(saleComps => {
            this.setSaleComps(saleComps);
        }).finally(() => {
            this.setIsSaleCompsLoading().fetching(false);
        });
    }

    async createSaleComp(saleCompState: ISaleCompState, pipedriveBuyerId: number | null, pipedriveSellerId: number | null, pipedriveBrokerageId: number | null, centerPoint: IGeoCoordinate, regridId: string | null): Promise<number> {
        this.setIsSaleCompsLoading().creating(true);
        try {
            const response = await this.saleCompsApiService.createSaleComp(saleCompState, pipedriveBuyerId, pipedriveSellerId, pipedriveBrokerageId, centerPoint, regridId);

            this.setSaleComps([
                ...this.saleComps,
                {
                    ...saleCompState,
                    address: saleCompState.address!,
                    regridId: regridId,
                    saleDate: saleCompState.saleDate!,
                    salePrice: saleCompState.salePrice!,
                    grossPerAcre: saleCompState.grossPerAcre!,
                    parcelSizeAcres: saleCompState.parcelSizeAcres!,
                    landSizeUsable: saleCompState.landSizeUsable!,
                    id: response.id,
                    pipedriveBuyerId: pipedriveBuyerId,
                    pipedriveSellerId: pipedriveSellerId,
                    pipedriveBrokerageId: pipedriveBrokerageId,
                    centerPoint: centerPoint,
                    modifiedByUser: response.modifiedByUser,
                    modifiedDate: response.modifiedDate,
                    attachments: [],
                },
            ]);

            return response.id;
        } finally {
            this.setIsSaleCompsLoading().creating(false);
        }
    }

    async updateSaleComp(saleComp: ISaleComp) {
        this.setIsSaleCompsLoading().updating(true);
        try {
            const response = await this.saleCompsApiService.updateSaleComp(saleComp);
            saleComp.modifiedDate = response.modifiedDate;
            saleComp.modifiedByUser = response.modifiedByUser;

            this.setSaleComps([
                ...this.saleComps.filter(sc => sc.id !== saleComp.id),
                saleComp,
            ]);
        } finally {
            this.setIsSaleCompsLoading().updating(false);
        }
    }

    async deleteSaleComp(id: number) {
        this.setIsSaleCompsLoading().deleting(true);
        try {
            await this.saleCompsApiService.deleteSaleComp(id);

            this.setSaleComps([
                ...this.saleComps.filter(sc => sc.id !== id),
            ]);
        } finally {
            this.setIsSaleCompsLoading().deleting(false);
        }
    }

    async uploadAttachment(saleCompId: number, file: File): Promise<void> {
        const attachment = await this.saleCompsApiService.uploadSaleCompAttachment(saleCompId, file);
        this.saleComps.find(sc => sc.id === saleCompId)!.attachments.push(attachment);
    }

    async deleteAttachment(saleCompId: number, key: string): Promise<void> {
        await this.saleCompsApiService.deleteSaleCompAttachment(saleCompId, key);
        const saleComp = this.saleComps.find(sc => sc.id === saleCompId)!;
        saleComp.attachments = saleComp.attachments.filter(a => a.key !== key);
    }

    clear() {
        this.setSaleComps([]);
        this.setEditedSaleCompId(null);
        this.isSaleCompsLoading = {};
    }
}